import {
  Autocomplete,
  Box,
  Switch,
  TextField,
  FormHelperText,
  FormGroup,
  FormControlLabel,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material'
import {
  IntegrationConfig,
  IntegrationConfigSettingsSendhubWebhookRepeater,
  SendhubWebhookType,
} from '@sendhub/integrations/api-client-typescript-axios'
import { getEnumValues } from '../../../../utils/get-enum-values'

import { useEffect, useState } from 'react'
import { fetchIntegrationConfigs, updateIntegrationConfig } from '../../../store/integration-config/integration-configs.slice'
import { useAppDispatch } from '../../../store/root'
import { Field, FieldArray, FormikProvider, useFormik } from 'formik'
import SelectAccount from '../../select-account'
import { FormSaveControls } from '../../save-status-indicator'
import { applyConfigUpdateError } from '../apply-config-update-error'
import { IntegrationsEnabledSwitch } from '../../integrations-enabled-switch'
import { useIntegrationsAccess } from '../../../store/integrations-access'
import {
  createServiceAccount,
  selectAllServiceAccounts,
  selectServiceAccountsLoadingStatus,
} from '../../../store/service-account/service-accounts.slice'
import { useSelector } from 'react-redux'
import { AddCircleOutlineOutlined, DeleteOutlined } from '@mui/icons-material'
import { sendhubApi as sh } from '../../../services/sendhub-api'
import * as yup from 'yup'

export function isSendhubWebhookRepeaterSettings(obj: unknown): obj is IntegrationConfigSettingsSendhubWebhookRepeater {
  return typeof obj === 'object' && obj !== null && 'urls' in obj
}

const validationSchema = yup.object({
  enabled: yup.boolean().default(false),
  integrationId: yup.string().required(),
  settings: yup.object({
    urls: yup
      .array(
        yup
          .object({
            url: yup.string().required().url().label('URL'),
            webhookTypes: yup
              .array(yup.string().required().oneOf(getEnumValues(SendhubWebhookType)))
              .min(1)
              .label('Webhook Types'),
          })
          .test('unique', 'Duplicate URL', function validateUnique(urlObj) {
            const parent: yup.InferType<
              yup.ArraySchema<
                | {
                    webhookTypes?: SendhubWebhookType[] | undefined
                    url: string
                  }[]
                | undefined,
                yup.AnyObject,
                undefined,
                ''
              >
            > = this.parent
            if (parent === undefined) return true
            const isDuplicate =
              parent.reduce((previousValue, currentValue) => (currentValue.url === urlObj.url ? previousValue + 1 : previousValue), 0) > 1

            return isDuplicate ? this.createError({ path: `${this.path}.url` }) : true
          })
      )
      .max(10),
  }),
  serviceAccountId: yup.string().required('Must select a SendHub Account').uuid(),
})

export interface ConfigureSendhubWebhookRepeaterProps {
  integrationConfig: IntegrationConfig
}

export function ConfigureSendhubWebhookRepeater({ integrationConfig }: ConfigureSendhubWebhookRepeaterProps) {
  const dispatch = useAppDispatch()
  const { canEdit } = useIntegrationsAccess()
  const serviceAccounts = useSelector(selectAllServiceAccounts)
  const sendhubServiceAccounts = serviceAccounts.filter((sA) => sA.integrationGroupId === 'sendhub')
  const serviceAccountsLoadingStatus = useSelector(selectServiceAccountsLoadingStatus)

  const startingSettings = () => (isSendhubWebhookRepeaterSettings(integrationConfig.settings) ? integrationConfig.settings : { urls: [] })
  const [initialValues, setInitialValues] = useState({ ...integrationConfig, settings: startingSettings() })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, setFieldError, setFieldTouched, resetForm }) => {
      setStatus(null)
      try {
        await dispatch(updateIntegrationConfig({ id: values.id, integrationConfig: values })).unwrap()
        resetForm({ values, status: { type: 'success' } })
      } catch (error) {
        applyConfigUpdateError(error, { setStatus, setFieldError, setFieldTouched })
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({ values: { ...integrationConfig, settings: startingSettings() } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationConfig])

  // Create sendhub serviceAccount if none
  useEffect(() => {
    if (canEdit && serviceAccountsLoadingStatus === 'loaded' && sendhubServiceAccounts.length === 0) {
      dispatch(
        createServiceAccount({
          integrationGroupId: 'sendhub',
          credentials: { apiKey: '' },
          nickname: `This SendHub Line`,
        })
      )
    }
  }, [canEdit, integrationConfig, sendhubServiceAccounts, serviceAccountsLoadingStatus, dispatch])

  // Select first serviceAccount automatically
  useEffect(() => {
    if (serviceAccountsLoadingStatus === 'loaded' && sendhubServiceAccounts.length > 0 && formik.values.serviceAccountId === undefined) {
      formik.setFieldValue('serviceAccountId', sendhubServiceAccounts[0].id)
    }
  }, [sendhubServiceAccounts, serviceAccountsLoadingStatus, formik.setFieldValue])

  // Allow removing all rows
  useEffect(() => {
    if (
      formik.values.settings.urls.length === 1 &&
      formik.values.settings.urls[0].url?.trim() === '' &&
      formik.values.settings.urls[0]?.webhookTypes?.length === 0
    ) {
      formik.setFieldValue(`settings.urls`, [])
    }
  }, [formik.values])

  useEffect(() => {
    dispatch(fetchIntegrationConfigs())
  }, [dispatch])

  const addEmptyRow = () => {
    formik.setFieldValue(`settings.urls`, [...formik.values.settings.urls, { url: '', webhookTypes: [] }], false)
  }

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <SelectAccount integrationConfig={integrationConfig} hideConnectAccountButton hideDeleteAccountButton />
        <Divider sx={{ marginY: 2 }} />
        <FieldArray
          name="settings.urls"
          render={(arrayHelpers) => (
            <>
              {(formik.values.settings.urls.length ? formik.values.settings.urls : [{ url: '', webhookTypes: [] }]).map((urlObj, index) => (
                <Stack
                  direction="row"
                  key={formik.values.settings.urls.length === 0 ? 'derp' : index}
                  sx={{ width: '100%', marginBottom: 2 }}
                  spacing={2}
                >
                  {/* URL field */}
                  <Field
                    name={`settings.urls.${index}.url`}
                    label="URL"
                    as={TextField}
                    disabled={!canEdit}
                    placeholder="https://example.com/my-webhook-endpoint"
                    sx={{ flexGrow: 1 }}
                    error={(() => {
                      const urlError = formik.errors.settings?.urls?.[index]
                      return (
                        formik.touched.settings?.urls?.[index]?.url &&
                        typeof urlError === 'object' &&
                        urlError !== null &&
                        Boolean(urlError.url)
                      )
                    })()}
                    helperText={(() => {
                      const urlError = formik.errors.settings?.urls?.[index]
                      return (
                        formik.touched.settings?.urls?.[index]?.url && typeof urlError === 'object' && urlError !== null && urlError.url
                      )
                    })()}
                  />

                  {/* Webhook Types field */}
                  <Autocomplete
                    sx={{ flexGrow: 1, maxWidth: 300 }}
                    disableClearable
                    openOnFocus
                    multiple
                    disableCloseOnSelect
                    disabled={!canEdit}
                    id={`settings.urls.${index}.webhookTypes`}
                    value={urlObj.webhookTypes || []}
                    onChange={(event, value) => formik.setFieldValue(`settings.urls.${index}.webhookTypes`, value)}
                    options={getEnumValues(SendhubWebhookType)}
                    getOptionDisabled={(option) => option.includes('Coming soon')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name={`settings.urls.${index}.webhookTypes`}
                        label="Webhook Types"
                        error={(() => {
                          const urlError = formik.errors.settings?.urls?.[index]
                          return (
                            formik.touched.settings?.urls?.[index]?.webhookTypes &&
                            typeof urlError === 'object' &&
                            urlError !== null &&
                            Boolean(urlError.webhookTypes)
                          )
                        })()}
                        helperText={(() => {
                          const urlError = formik.errors.settings?.urls?.[index]
                          return (
                            formik.touched.settings?.urls?.[index]?.webhookTypes &&
                            typeof urlError === 'object' &&
                            urlError !== null &&
                            urlError.webhookTypes
                          )
                        })()}
                      />
                    )}
                  />

                  <Box>
                    {canEdit && (
                      <Tooltip title="Remove Webhook URL" placement="top">
                        <IconButton
                          aria-label={'Remove Webhook URL'}
                          onClick={() => arrayHelpers.remove(index)}
                          sx={{
                            marginTop: 1,
                          }}
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Stack>
              ))}
              <Box sx={{ textAlign: 'center' }}>
                <Tooltip title="Add Webhook URL" placement="top">
                  <Button
                    aria-label={'Add Webhook URL'}
                    disabled={!canEdit || formik.values.settings.urls.length === 0}
                    onClick={() => {
                      addEmptyRow()
                    }}
                    size={'small'}
                  >
                    <AddCircleOutlineOutlined />
                  </Button>
                </Tooltip>
              </Box>
            </>
          )}
        />

        <FormGroup>
          <IntegrationsEnabledSwitch />
        </FormGroup>
        <FormSaveControls />
      </form>
    </FormikProvider>
  )
}
